import { all, call, put, race, take, takeEvery, takeLatest } from "redux-saga/effects"

import {
  GET_ANALYTICS, GET_ANALYTICS_EXPORT, GET_CALL_OPPORTUNITIES, GET_CLICK_CONV_AGGR_ANALYTICS,
  RESET_ANALYTICS,
} from "./actionTypes"

import {
  getAnalyticsSuccess,
  getAnalyticsError,
  getAnalyticsExportSuccess,
  getAnalyticsExportError,
  getClickConvAggrAnalyticsSuccess,
  getClickConvAggrAnalyticsError,
  getCompareToAnalyticsSuccess,
  getCompareToClickConvAggrAnalyticsSuccess,
  getCallOpportunitiesSuccess,
  getCompareToCallOpportunitiesSuccess,
  getCallOpportunitiesError
} from "./actions"

import { genericPostRequest, genericPostRequestFeatures } from "helpers/sf_backend_helper.js"

import { AppConfiguration, isRtbBrand } from "config"
import { NAME_OF_PRODUCT_TYPES } from "helpers/sf_constants"

export function* onGetAnalytics(action) {
  try {
    const { skip_total_response, ...analyticsPayload } = action.payload || {};
    let endpoint = '/api/v1/analytics'
    switch(action.product_type) {
      case NAME_OF_PRODUCT_TYPES.CALLS:
        endpoint = '/api/v1/analytics/calls'
        break
      case NAME_OF_PRODUCT_TYPES.LEADS:
        endpoint = '/api/v1/analytics/leads'
        break
    }
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const basePayload = {
      body: JSON.stringify({
        ...analyticsPayload
      }),
      headers: {
        Authorization: user.token,
        'account-id': user.account_id,
        'Content-Type': 'application/json'
      }
    };

    // Determine if we need the total response
    const shouldFetchTotalResponse =
      !skip_total_response &&
      !(analyticsPayload?.individual_clicks ||
        analyticsPayload?.individual_calls ||
        analyticsPayload?.individual_leads ||
        analyticsPayload?.group_by_params?.length === 0);

    // Parallel API calls
    let [response, total_response] = yield all([
      call(
        genericPostRequestFeatures,
        `${AppConfiguration.BACKEND_URL}${endpoint}`,
        basePayload
      ),
      shouldFetchTotalResponse 
        ? call(
            genericPostRequestFeatures,
            `${AppConfiguration.BACKEND_URL}${endpoint}`,
            {
              ...basePayload,
              body: JSON.stringify({
                ...analyticsPayload,
                group_by_params: [],
                ...(isRtbBrand && endpoint === '/api/v1/analytics' && analyticsPayload?.group_by_params?.length
                  ? { rtb_total_group_by_params: analyticsPayload.group_by_params }
                  : {}),
                // Totals row is a single aggregate; never paginate it.
                page_number: undefined,
                page_size: undefined
              })
            }
          )
        : Promise.resolve([])
    ]);

    // Backend paginates only when page params are sent: an array is unpaginated,
    // { data, additional_data } is a page. Normalize both to rows + meta.
    const rows = Array.isArray(response) ? response : (response?.data ?? [])
    const pagination = Array.isArray(response) ? null : (response?.additional_data ?? null)
    let normalized_total = Array.isArray(total_response) ? total_response : (total_response?.data ?? [])

    if(analyticsPayload?.group_by_params?.length === 0) {
      normalized_total = rows;
    }
    if (action?.compare_to) {
      yield put(getCompareToAnalyticsSuccess(rows, normalized_total, pagination))
    } else {
      yield put(getAnalyticsSuccess(rows, shouldFetchTotalResponse ? normalized_total : undefined, pagination, action.request_id))
    }
  } catch (error) {
    yield put(getAnalyticsError(error, action.request_id))
  }
}

export function* onGetAnalyticsExport(action) {
  try {
    const endpoint = `/api/v1/analytics/${action.product_type}/export`
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}${endpoint}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'Content-Type': 'application/json'
        }
      }
    )

    yield put(getAnalyticsExportSuccess(response))
  } catch (error) {
    yield put(getAnalyticsExportError(error))
  }
}

export function* onGetClickConvAggrAnalytics(action) {
  const product_type = action?.product_type || NAME_OF_PRODUCT_TYPES.CLICKS
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const endpoint = `/api/v1/pixel_agg_stats/${product_type.split(' ').join('_')}`
    const basePayload = {
      body: JSON.stringify({
        ...action.payload
      }),
      headers: {
        Authorization: user.token,
        'account-id': user.account_id,
        'Content-Type': 'application/json'
      }
    };
    // Determine if we need the total response
    const shouldFetchTotalResponse = (action?.payload?.group_by_params?.length !== 0);
    let [response, total_response] = yield all([
      call(
        genericPostRequest, 
        `${AppConfiguration.BACKEND_URL}${endpoint}`, 
        basePayload
      ),
      shouldFetchTotalResponse 
        ? call(
            genericPostRequest, 
            `${AppConfiguration.BACKEND_URL}${endpoint}`, 
            {
              ...basePayload,
              body: JSON.stringify({
                ...action.payload,
                group_by_params: []
              })
            }
          )
        : Promise.resolve([])
    ]);

    if (!shouldFetchTotalResponse) {
      total_response = response;
    }
    if (action?.compare_to) {
      yield put(getCompareToClickConvAggrAnalyticsSuccess(response, total_response, product_type))
    } else {
      yield put(getClickConvAggrAnalyticsSuccess(response, total_response, product_type))
    }
  } catch (error) {
    yield put(getClickConvAggrAnalyticsError(error))
  }
}

export function* onGetCallOpportunities(action) {
  try {
    let user = JSON.parse(localStorage.getItem("authUser")).user
    const basePayload = {
      body: JSON.stringify({
        ...action.payload
      }),
      headers: {
        Authorization: user.token,
        'account-id': user.account_id,
        'Content-Type': 'application/json'
      }
    };

    // Determine if we need the total response
    const shouldFetchTotalResponse = 
      !(action?.payload?.individual_clicks || 
        action?.payload?.group_by_params?.length === 0);

    // Parallel API calls
    let [response, total_response] = yield all([
      call(
        genericPostRequest, 
        `${AppConfiguration.BACKEND_URL}/api/v1/analytics/calls/opportunities`, 
        basePayload
      ),
      shouldFetchTotalResponse 
        ? call(
            genericPostRequest, 
            `${AppConfiguration.BACKEND_URL}/api/v1/analytics/calls/opportunities`, 
            {
              ...basePayload,
              body: JSON.stringify({
                ...action.payload,
                group_by_params: []
              })
            }
          )
        : Promise.resolve([])
    ]);

    if(action?.payload?.group_by_params?.length === 0) {
      total_response = response;
    }
    if (action?.compare_to) {
      yield put(getCompareToCallOpportunitiesSuccess(response, total_response))
    } else {
      yield put(getCallOpportunitiesSuccess(response, total_response))
    }
  } catch (error) {
    yield put(getCallOpportunitiesError(error))
  }
}


export function* cancelOnAnalyticsReset(worker, action) {
  yield race({
    request: call(worker, action),
    reset: take(RESET_ANALYTICS),
  })
}

export const isMainAnalyticsAction = action => action.type === GET_ANALYTICS && !action.compare_to
export const isCompareToAnalyticsAction = action => action.type === GET_ANALYTICS && action.compare_to
export const isMainClickConvAggrAnalyticsAction = action =>
  action.type === GET_CLICK_CONV_AGGR_ANALYTICS && !action.compare_to
export const isCompareToClickConvAggrAnalyticsAction = action =>
  action.type === GET_CLICK_CONV_AGGR_ANALYTICS && action.compare_to

function* analyticsSaga() {
  yield takeLatest(isMainAnalyticsAction, onGetAnalytics)
  yield takeLatest(isCompareToAnalyticsAction, onGetAnalytics)
  yield takeEvery(GET_ANALYTICS_EXPORT, onGetAnalyticsExport)
  yield takeLatest(isMainClickConvAggrAnalyticsAction, cancelOnAnalyticsReset, onGetClickConvAggrAnalytics)
  yield takeLatest(isCompareToClickConvAggrAnalyticsAction, cancelOnAnalyticsReset, onGetClickConvAggrAnalytics)
  yield takeEvery(GET_CALL_OPPORTUNITIES, cancelOnAnalyticsReset, onGetCallOpportunities)
}

export default analyticsSaga
